import React, { useState } from 'react';
import { Box, Drawer, Card, CardContent, CardHeader, Typography, CircularProgress } from '@mui/material';
import api from "../../../../services/api";
import FormAddEditToolbar from "../../../../components/global/AddEditBar";
import { useNavigate } from 'react-router-dom';
import { useNotifier } from "muinotify";
import FieldsForm from "../../FieldsForm";
import useSWR, { mutate } from 'swr';
import { useParams } from "react-router";
import ErrorNotification from '../../../../components/ErrorNotification/ErrorNotification';
import * as yup from 'yup';

const validationSchema = yup.object().shape({
  p_descr: yup.string().required('Το πεδίο είναι υποχρεωτικό'),
  no_of_episodes: yup.number().required('Το πεδίο είναι υποχρεωτικό').positive('Πρέπει να είναι θετικός αριθμός'),
  p_aa: yup.number().nullable(true),
  episode_duration: yup.number().required('Το πεδίο είναι υποχρεωτικό').positive('Πρέπει να είναι θετικός αριθμός'),
  episodes_per_recording: yup
    .number()
    .nullable(true)
    .positive('Πρέπει να είναι θετικός αριθμός')
    .integer('Πρέπει να είναι ακέραιος'),
  locked_episodes: yup.array().of(yup.number().positive()).nullable(true),
});

const headerNames = {
  p_descr: 'Περιγραφή παραγωγής',
  no_of_episodes: 'Αριθμός επεισοδίων',
  p_aa: 'ΑΑ',
  episode_duration: 'Διάρκεια επεισοδίου',
  episodes_per_recording: 'Επεισόδια ανά εγγραφή (σχεδιασμός)',
  locked_episodes: 'Κλειδωμένα Επεισόδια'
};

const URL = `productions`;

const ProductionEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notifier } = useNotifier();
  const [state, setState] = useState(null);
  const [errorMessage, setErrorMessage] = useState();
  const [errors, setErrors] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);

  const clientKey = `/${URL}/${id}`;

  if (!state) {
    mutate(clientKey);
  }

  const { data: productions } = useSWR(clientKey, {
    revalidateOnFocus: false,
    revalidateOnMount: true,
    onSuccess: (data) => {
      if (data) {
        setState(data);
        setDrawerOpen(true);
        setIsUpdating(false);
      }
    },
    onError: (err) => {
      setErrorMessage(err);
    }
  });

  function updateField(fieldName, value) {
    setState({ ...state, [fieldName]: value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await validationSchema.validate(state, { abortEarly: false });
      setErrors({});
      setIsUpdating(true);


      api.patch(`/${URL}/${state.id}`, state)
        .then(response => {
          mutate(response);
          notifier("Record updated.", { variant: "success" });
          setDrawerOpen(false);
          navigate(-1);
        })
        .catch(error => {
          setErrorMessage(error);
          notifier("Error: " + error, { variant: "error" });
        })
        .finally(() => {
          setIsUpdating(false);
        });

    } catch (validationErrors) {
      const formattedErrors = {};
      validationErrors.inner.forEach((error) => {
        formattedErrors[error.path] = error.message;
      });
      setErrors(formattedErrors);
    }
  };

  const handleSave = () => {
    const form = document.getElementById(`${URL}-form`);
    if (form) {
      form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const [drawerOpen, setDrawerOpen] = useState(true);

  return (
    <>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCancel}
        PaperProps={{
          style: {
            width: '100%',
            height: '100vh',
            maxWidth: '800px',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Card
          sx={{
            width: '100%',
            maxWidth: 800,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden', // Prevent overflow from Card itself
          }}
        >
          {/* Header Section */}
          <Box display="flex" justifyContent="center" alignItems="center" width="100%">
            <CardHeader
              title={<Typography variant="h3" gutterBottom>Επεξεργασία Παραγωγής</Typography>}
            />
          </Box>

          {/* Scrollable Content Section */}
          <CardContent
            sx={{
              display: 'flex',
              height: '100vh',
              flexGrow: 1, // Allow this section to grow and shrink
              overflowY: 'auto',
              margin: 'auto',
            }}
          >
            <form id={`${URL}-form`} onSubmit={handleSubmit}>
              <FieldsForm
                state={state}
                errors={errors}
                updateField={updateField}
                headerNames={headerNames}
              />
            </form>
            {isUpdating && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  zIndex: 1000,
                }}
              >
                <CircularProgress />
              </Box>
            )}
          </CardContent>

          {/* Toolbar (Footer Section) */}
          <Box>
            <FormAddEditToolbar
              handleSave={handleSave}
              handleCancel={handleCancel}

            />
          </Box>
        </Card>
      </Drawer>

    </>
  );
};

export default ProductionEdit;
